/**
 * Read-only view of the builder while another admin holds the edit lock:
 * a banner naming them, and a wrapper that lets the admin scroll and look but
 * swallows every click, key press, drag and focus before it reaches the
 * builder. Everything underneath keeps rendering as normal.
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useEditLock } from './ProgramEditLockProvider';

export const editLockMessage = (name?: string) =>
  `${name || 'Another admin'} is currently in the editor. Ask them to save changes and leave before you proceed.`;

export const EditLockBanner: React.FC<{ sx?: object }> = ({ sx }) => {
  const { status, holder } = useEditLock();
  if (status !== 'locked') return null;
  return (
    <Box
      role="status"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: 2,
        py: 1.25,
        borderRadius: '8px',
        border: '1px solid rgba(247, 148, 32, 0.45)',
        background: 'rgba(247, 148, 32, 0.08)',
        ...sx
      }}
    >
      <LockOutlinedIcon sx={{ fontSize: 18, color: '#F79420', flexShrink: 0 }} />
      <Typography sx={{ fontFamily: 'Inter', fontSize: 13, color: '#FDFDFD', lineHeight: '18px' }}>
        {editLockMessage(holder?.name)}
      </Typography>
    </Box>
  );
};

// Marks UI that stays usable in the read-only view (e.g. Invite Admin). Put it
// on portal roots too — dialogs/menus render outside the guarded DOM subtree.
export const EDIT_LOCK_EXEMPT = { 'data-edit-lock-exempt': true } as const;

const isExempt = (e: React.SyntheticEvent) =>
  Boolean((e.target as HTMLElement)?.closest?.('[data-edit-lock-exempt]'));

const swallow = (e: React.SyntheticEvent) => {
  if (isExempt(e)) return;
  e.preventDefault();
  e.stopPropagation();
};

// Keys that only scroll — everything else could edit.
const SCROLL_KEYS = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ']);

export const ReadOnlyWhenLocked: React.FC<{ children: React.ReactNode; sx?: object }> = ({ children, sx }) => {
  const { status } = useEditLock();
  const locked = status === 'locked';
  if (!locked) return <>{children}</>;
  return (
    <Box
      aria-readonly
      sx={{ display: 'contents', ...sx }}
      onClickCapture={swallow}
      onDoubleClickCapture={swallow}
      onMouseDownCapture={swallow}
      onPointerDownCapture={swallow}
      onDragStartCapture={swallow}
      onDropCapture={swallow}
      onPasteCapture={swallow}
      onChangeCapture={swallow}
      onKeyDownCapture={(e: React.KeyboardEvent) => {
        if (!SCROLL_KEYS.has(e.key) || (e.target as HTMLElement).isContentEditable) swallow(e);
      }}
      onFocusCapture={(e: React.FocusEvent) => {
        if (isExempt(e)) return;
        const el = e.target as HTMLElement;
        if (el.matches('input, textarea, select, [contenteditable="true"]')) el.blur();
      }}
    >
      {children}
    </Box>
  );
};
